import React, { useCallback, useId, useState } from "react";
import {
  AppBar,
  Box,
  ButtonBase,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";

import { HOME_HERO_MAX_WIDTH_PX } from "./homeConstants";

const POSTER_MAX_HEIGHT_VH = { xs: 38, sm: 50, md: 54 } as const;
const POSTER_MAX_HEIGHT_VH_COMPACT = { xs: 32, sm: 42, md: 46 } as const;

export type HomePosterInteractiveProps = {
  compact?: boolean;
  sx?: SxProps<Theme>;
  hintText?: string;
  posterUrl?: string;
  posterAlt?: string;
};

export const HomePosterInteractive: React.FC<HomePosterInteractiveProps> = ({
  compact = false,
  sx,
  hintText = "Toucher ou cliquer pour agrandir — lire tableaux & horaires",
  posterUrl = "/2026.png",
  posterAlt = "Affiche du tournoi national SQY Ping 2026 — dates, lieu, dotations et grilles horaires",
}) => {
  const theme = useTheme();
  const titleId = useId();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));
  const isDark = theme.palette.mode === "dark";
  const vh = compact ? POSTER_MAX_HEIGHT_VH_COMPACT : POSTER_MAX_HEIGHT_VH;

  const openLightbox = useCallback(() => setLightboxOpen(true), []);
  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  return (
    <>
      <ButtonBase
        type="button"
        onClick={openLightbox}
        focusRipple
        aria-haspopup="dialog"
        aria-expanded={lightboxOpen}
        aria-label="Agrandir l’affiche pour lire le détail du programme"
        sx={{
          display: "block",
          width: "100%",
          maxWidth: `min(100%, ${HOME_HERO_MAX_WIDTH_PX}px)`,
          mx: "auto",
          my: { xs: 2, sm: 2.5 },
          borderRadius: 2,
          overflow: "hidden",
          position: "relative",
          bgcolor: isDark
            ? alpha(theme.palette.common.white, 0.06)
            : alpha(theme.palette.common.black, 0.04),
          border: `1px solid ${
            isDark
              ? alpha(theme.palette.primary.light, 0.35)
              : alpha(theme.palette.primary.main, 0.2)
          }`,
          transition: "box-shadow 0.3s ease, transform 0.3s ease, border-color 0.2s",
          "@media (hover: hover)": {
            "&:hover": {
              boxShadow: theme.shadows[8],
              borderColor: alpha(theme.palette.primary.main, 0.45),
              "& .home-poster-image": {
                transform: "scale(1.045)",
              },
              "& .home-poster-hint": {
                opacity: 1,
              },
            },
          },
          ...sx,
        }}
      >
        <Box
          component="img"
          className="home-poster-image"
          src={posterUrl}
          alt={posterAlt}
          sx={{
            display: "block",
            width: "auto",
            maxWidth: "100%",
            maxHeight: {
              xs: `${vh.xs}vh`,
              sm: `${vh.sm}vh`,
              md: `${vh.md}vh`,
            },
            height: "auto",
            mx: "auto",
            objectFit: "contain",
            transition: "transform 0.45s cubic-bezier(0.33, 1, 0.68, 1)",
          }}
          loading="lazy"
        />
        <Stack
          className="home-poster-hint"
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={0.75}
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            py: 1,
            px: 1.5,
            background: `linear-gradient(transparent, ${alpha(
              theme.palette.common.black,
              0.72
            )})`,
            color: "common.white",
            opacity: { xs: 1, sm: 0.92 },
            "@media (hover: hover)": {
              opacity: 0,
              transition: "opacity 0.25s ease",
            },
          }}
        >
          <ZoomInIcon sx={{ fontSize: 20 }} aria-hidden />
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            {hintText}
          </Typography>
        </Stack>
      </ButtonBase>

      <Dialog
        open={lightboxOpen}
        onClose={closeLightbox}
        fullScreen={isSmDown}
        maxWidth="md"
        fullWidth
        scroll="paper"
        aria-labelledby={titleId}
        PaperProps={{
          sx: {
            bgcolor: isDark ? "grey.900" : "grey.100",
            ...(isSmDown && { m: 0, maxHeight: "100%" }),
          },
        }}
      >
        <AppBar
          position="sticky"
          elevation={0}
          color="inherit"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Toolbar variant="dense" sx={{ gap: 1 }}>
            <Typography
              id={titleId}
              variant="subtitle1"
              component="span"
              sx={{ flex: 1, fontWeight: 700 }}
            >
              Affiche — vue agrandie
            </Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={closeLightbox}
              aria-label="Fermer l’affiche agrandie"
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>
        <DialogContent
          sx={{
            p: { xs: 1, sm: 2 },
            overflow: "auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
          }}
        >
          <Box
            component="img"
            src={posterUrl}
            alt={posterAlt}
            sx={{
              width: "100%",
              maxWidth: 640,
              height: "auto",
              objectFit: "contain",
              borderRadius: 1,
              boxShadow: isDark ? 4 : 2,
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
